import { useEffect, useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import { Comment as CommentType } from '../../../../../store/comment';

import { IconCommentMenu } from '../../../Icon';
import CommentForm from '../../CommentForm';
import {
  useCommentChildrenList,
  useCommentDelete,
  useCommentReaction,
  useCommentReactionDelete,
} from '../../../../../hooks/api/comments';
import {
  DefaultFetchError,
  getMessageInError,
} from '../../../../../hooks/fetch';
import { PaginationProps, Reaction } from '../../../../../store/types';
import { CommentOrderColumn } from '../../../../../store/comment/children';
import { useAuth } from '../../../../../store/auth';
import { ModalReport } from '../../../ModalReport';
import User from '../../../User';
import Reactions from '../../../Reactions';
import Replies from '../../../Replies';
import useAuthModals from '../../../../Layout/Menu/useAuthModals';
import { useActionGuard } from '../../../../../hooks/useActionGuard';
import Carousel from '../../../Carousel'; // ← use app carousel for images

import styles from './index.module.scss';

interface CommentProps {
  comment: CommentType;
  isOwner?: boolean;
  ownerId?: string;
  hideReply?: boolean;
  onRepplyAdded?: () => void;
  onRepplyDeleted?: () => void;
  onCommentDeleted: () => void;
  onReactionChange: (params: Reaction | null) => void;
  onEdited: (comment: CommentType) => void;
}

function Comment({
  comment,
  isOwner,
  ownerId,
  hideReply,
  onRepplyAdded,
  onRepplyDeleted,
  onCommentDeleted,
  onReactionChange,
  onEdited,
}: CommentProps) {
  const { user, authorized } = useAuth();
  const { content, openLoginModal } = useAuthModals();

  const [isOpenReportModal, setIsOpenReportModal] = useState(false);
  const [isOpenReplyList, setIsOpenReplyList] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [pagination, setPagination] = useState<
    PaginationProps<CommentOrderColumn>
  >({
    page: 1,
    pageSize: 10,
    orderBy: 'ASC',
    orderByColumn: 'createdAt',
  });

  // Anti-spam guard: in-flight lock, cooldown, dedupe, optional queue
  const { guard, coolingDown, isBusy } = useActionGuard({
    cooldownMs: 700,
    dedupeWindowMs: 500,
    enqueueLatest: true,
  });

  // Require auth before running an action
  const withAuth = <Args extends any[]>(fn: (...args: Args) => void | Promise<void>) => (...args: Args) => {
    if (!authorized) {
      openLoginModal(); // shows login modal

      return;
    }

    // eslint-disable-next-line consistent-return
    return fn(...args);
  };

  const commentReaction = useCommentReaction();
  const commentReactionDelete = useCommentReactionDelete();
  const commentChildrenList = useCommentChildrenList(comment.id);
  const commentDelete = useCommentDelete(comment.id);

  const [childrenListData, setChildrenListData] = useState<CommentType[]>([]);

  // Load replies when the list is opened
  useEffect(() => {
    if (isOpenReplyList && comment.numberOfCommentsTotal > 0) {
      commentChildrenList.fetch(pagination);
    }
  }, [isOpenReplyList, pagination]);

  // Reset replies when closed
  useEffect(() => {
    if (!isOpenReplyList) {
      setChildrenListData([]);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [isOpenReplyList]);

  // Errors from replies API
  useEffect(() => {
    if (commentChildrenList.error) {
      message.error(getMessageInError(commentChildrenList.error)).then(() => {
        commentChildrenList.clearError();
      });
    }
  }, [commentChildrenList.error]);

  // Merge/append replies
  useEffect(() => {
    if (commentChildrenList.response?.data) {
      if (commentChildrenList.response.data.meta.page === 1) {
        setChildrenListData(commentChildrenList.response.data.data || []);
      } else {
        setChildrenListData((prev) => {
          const newData = commentChildrenList.response?.data.data || [];
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNew = newData.filter(
            (item: CommentType) => !existingIds.has(item.id),
          );

          return [...prev, ...uniqueNew];
        });
      }
    }
  }, [commentChildrenList.response?.data]);

  const onSubmitNewComment = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    onRepplyAdded?.();
    setIsOpenReplyList(true);
  };

  // --- Reaction API helpers ---

  const sendLike = async () => {
    const data = await commentReaction.fetch({
      commentId: comment.id,
      status: true,
    });

    if (data) onReactionChange({ id: data.id, status: true });
  };

  const sendDislike = async () => {
    const data = await commentReaction.fetch({
      commentId: comment.id,
      status: false,
    });

    if (data) onReactionChange({ id: data.id, status: false });
  };

  const deleteReaction = async (id: string) => {
    await commentReactionDelete.fetch(id);
    onReactionChange(null);
  };

  // --- Public handlers (guarded + auth-gated) ---

  const like = withAuth(
    guard(`comment-like:${comment.id}`, async () => {
      try {
        const current = comment.reactions?.[0];

        // If currently disliked -> remove then like
        if (current?.status === false) {
          const del = await commentReactionDelete.fetch(current.id);

          if (!del) return;
        }
        await sendLike();
      } catch (error) {
        message.error(getMessageInError(error as DefaultFetchError));
      }
    }),
  );

  const dislike = withAuth(
    guard(`comment-dislike:${comment.id}`, async () => {
      try {
        const current = comment.reactions?.[0];

        // If currently liked -> remove then dislike
        if (current?.status === true) {
          const del = await commentReactionDelete.fetch(current.id);

          if (!del) return;
        }
        await sendDislike();
      } catch (error) {
        message.error(getMessageInError(error as DefaultFetchError));
      }
    }),
  );

  const removeReaction = withAuth(
    guard(`comment-remove-reaction:${comment.id}`, async (id: string) => {
      try {
        await deleteReaction(id);
      } catch (error) {
        message.error(getMessageInError(error as DefaultFetchError));
      }
    }),
  );

  const removeComment = () => {
    commentDelete
      .fetch()
      .then(() => {
        message.success('Comment deleted successfully');
        onCommentDeleted();
        if (hideReply) onRepplyDeleted?.();
      })
      .catch((error: DefaultFetchError) => {
        message.error(getMessageInError(error));
      });
  };

  return (
    <div key={comment.id} className={styles.wrapper}>
      <div className={styles.header}>
        <User user={comment.user} date={comment.createdAt} isOwner={isOwner} />
        <Dropdown
          placement="bottomRight"
          menu={{
            items: [
              comment.user.id !== user?.id
                ? {
                  key: '0',
                  label: 'Report',
                  onClick: () => setIsOpenReportModal(true),
                }
                : null,
              comment.user.id === user?.id
                ? {
                  key: '1',
                  label: isEdit ? 'Cancel edit' : 'Edit',
                  onClick: () => setIsEdit((edit) => !edit),
                }
                : null,
              comment.user.id === user?.id
                ? {
                  key: '3',
                  label: 'Delete',
                  onClick: removeComment,
                }
                : null,
            ],
          }}
          trigger={['click']}
        >
          <Button size="large" className={styles.menuButton}>
            <IconCommentMenu />
          </Button>
        </Dropdown>
        {isOpenReportModal && (
          <ModalReport
            isOpen={isOpenReportModal}
            onCancel={() => setIsOpenReportModal(false)}
            createBody={{
              type: 'comment',
              commentId: comment.id,
            }}
          />
        )}
      </div>

      <div className={styles.body}>
        {isEdit ? (
          <CommentForm
            createParams={{
              body: {},
              parentId: comment.id,
            }}
            updateParams={{ id: comment.id }}
            initialValues={{
              message: comment.text,
              files: comment.files,
            }}
            onSuccess={(newComment: Partial<CommentType>) => {
              onEdited({ ...comment, ...newComment });
              setIsEdit(false);
            }}
          />
        ) : (
          <>
            {comment.files.length > 0 ? (
              <div
                className={styles.carousel}
                style={{
                  width: '100%',
                  maxWidth: '350px',
                  // optional minHeight like in Post to prevent flicker
                  minHeight: comment.files?.[0]?.location ? '272px' : undefined,
                }}
              >
                <Carousel files={comment.files} />
              </div>
            ) : null}

            <p className={styles.text}>{comment.text}</p>
          </>
        )}

        <Reactions
          commentCreateParams={{
            parentId: comment.id,
            body: { entity: comment.entity },
          }}
          reactions={comment.reactions}
          numberOfLikes={comment.numberOfLikes}
          numberOfDislikes={comment.numberOfDislikes}
          onLike={() => like()}
          onDislike={() => dislike()}
          removeReaction={(id) => removeReaction(id)}
          hideReply={hideReply}
          onSubmitNewComment={onSubmitNewComment}
          // Disable buttons while busy/cooling or API loading
          disabled={
            isBusy()
            || coolingDown
            || commentReaction.loading
            || commentReactionDelete.loading
          }
        />

        {/* Mount the auth modal content here (or in a higher-level Layout once) */}
        {content}

        <Replies
          ownerId={ownerId}
          isOpen={isOpenReplyList}
          onOpenChange={setIsOpenReplyList}
          numberOfCommentsTotal={comment.numberOfCommentsTotal}
          parentId={comment.id}
          onRepplyDeleted={onRepplyDeleted}
          loading={commentChildrenList.loading}
          onLoadMore={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
          hasNextPage={!!commentChildrenList.response?.data?.meta?.hasNextPage}
          comments={childrenListData}
          setComments={setChildrenListData}
        />
      </div>
    </div>
  );
}

export default Comment;
